/**
 * Module dependencies.
 */

import { Button, Type } from 'src/components/core';
import { ReactElement, ReactNode, useEffect } from 'react';
import { StepPageConfig } from 'src/types/advisor';
import { color, media, units } from '@untile/react-components';
import { ifProp } from 'styled-tools';
import { useAdvisor } from 'src/context/advisor/context';
import { useLocomotiveScroll } from 'react-locomotive-scroll';
import styled, { css } from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = StepPageConfig & {
  canContinue?: boolean;
  children: ReactNode;
  className?: string;
  isFirstStep?: boolean;
  isLastStep?: boolean;
  onContinue: () => void;
  onGoBack: () => void;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: grid;
  grid-template-rows: repeat(2, max-content) 1fr max-content;
  height: 100%;
  padding-bottom: ${units(6)};
  position: relative;
  width: 100%;

  ${media.min('md')`
    grid-template-rows: repeat(4, max-content);
    padding: 0 calc(100% / 8) ${units(18)};
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H4)`
  color: ${color('black')};
  margin-bottom: ${units(1)};
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.Paragraph)`
  color: ${color('grey500')};
  margin-bottom: ${units(4)};

  ${media.min('md')`
    margin-bottom: ${units(6)};
  `}
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div`
  height: max-content;
`;

/**
 * `ButtonsWrapper` styled component.
 */

const ButtonsWrapper = styled.div<{ hasTwoButtons: boolean }>`
  display: grid;
  grid-column-gap: ${units(2)};
  grid-template-columns: repeat(2, max-content);

  ${media.max('md')`
    margin-bottom: ${units(5)};

    ${ifProp(
      'hasTwoButtons',
      css`
        grid-column-gap: ${units(1)};
        grid-template-columns: repeat(2, 1fr);
      `,
      css`
        grid-template-columns: 100%;
      `
    )}
  `}
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  ${media.max('md')`
    width: unset;
  `}
`;

/**
 * `BaseStep` component.
 */

const BaseStep = (props: Props): ReactElement => {
  const {
    canContinue = true,
    children,
    className,
    description,
    isFirstStep = false,
    isLastStep,
    onContinue,
    onGoBack,
    title
  } = props;

  const { translate } = useTranslate();
  const { visibleStep } = useAdvisor();
  const locomotiveScroll = useLocomotiveScroll();

  useEffect(() => {
    if (locomotiveScroll?.scroll) {
      locomotiveScroll.scroll.scrollTo('top', { duration: 200 });
    }
  }, [locomotiveScroll, visibleStep]);

  return (
    <Wrapper className={className}>
      <Title>{translate(title)}</Title>

      <Description>{translate(description)}</Description>

      <ContentWrapper>{children}</ContentWrapper>

      <ButtonsWrapper hasTwoButtons={!isFirstStep}>
        {!isFirstStep && (
          <StyledButton
            colorTheme={'secondary'}
            onClick={onGoBack}
            size={'medium'}
          >
            {translate('common:actions.goBack')}
          </StyledButton>
        )}

        {!isLastStep && (
          <StyledButton
            disabled={!canContinue}
            onClick={onContinue}
            size={'medium'}
          >
            {translate('common:actions.continue')}
          </StyledButton>
        )}
      </ButtonsWrapper>
    </Wrapper>
  );
};

/**
 * Export `BaseStep` component.
 */

export default BaseStep;
